import { useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Box, CloseIcon, Divider, HStack, IconButton, Spinner, VStack } from 'native-base';
import { find, isEmpty } from 'lodash';
import moment from 'moment';

import { Button, ChevronLeftDoubleIcon, ChevronRightDoubleIcon, Modal, RefreshIcon, Text } from '@pimm/base';
import { EquipmentInstancesDto, LinkedInstanceChartDataDto, SensorConfigDto } from '@pimm/services/lib/store-equipment';
import { tempValue } from '@app/utils/string-formatter';
import { ResourceLoader } from '@app/components/shared';
import { useSiteTime } from '@app/features/store-core';
import { MsExcel as MsExcelIcon } from '@app/features/app-insights/icons/ms-excel';
import {
  buildEquipmentExportRows,
  buildEquipmentInfoRows,
  buildEventLogRows,
  buildExportFileName,
  clampChartDataByRange,
  clampStoreEventsByRange,
  snapToFiveMinutes,
} from '../_helper';
import { useEquipmentTranslate, useExportTo, useGetChartData, useGetEventCounts, useGetStoreEvents } from '../hooks';
import { AlertOctagonIcon, OutlineBadgeCheckIcon, PrinterIcon, ZoomInIcon, ZoomOutIcon } from '../icons';
import { SiteInfo, SensorTypeMap } from '../types';
import { ChartStatistics } from './chart-statistics';
import { DateTimeRangeBar } from './date-time-range-bar';
import { EventLog, EventLogs } from './event-logs';
import { ReportPrintView } from './report-print-view';
import { TemperatureChart } from './temperature-chart';

export type EquipmentChartDataProps = {
  equipmentFocus: EquipmentInstancesDto;
  siteId: string;
  hardwareSensors?: SensorConfigDto[]; // Used by the Excel export to populate the Equipment Information sheet (model, S/N)
  // List collapse state + toggle handler. Owned by EquipmentStatus so the
  // chart panel's title-row toggle button can drive the sibling list
  // rail's visibility. Optional so EquipmentChartData still renders
  // standalone in stories / Diagnostics / Performance tabs where there's
  // no list rail to collapse.
  listCollapsed?: boolean;
  onToggleList?: () => void;
  siteInfo?: SiteInfo;
  generatedBy?: string;
};

export const EquipmentChartData = ({
  equipmentFocus,
  siteId,
  hardwareSensors,
  listCollapsed,
  onToggleList,
  siteInfo,
  generatedBy,
}: EquipmentChartDataProps) => {
  const { t } = useTranslation();
  const { et } = useEquipmentTranslate();
  const siteTime = useSiteTime();

  // The DateTimeRangePicker popover positions itself with `position: fixed`
  // relative to this chart panel's left/right edges — pass the panel ref down.
  const panelRef = useRef<any>(null);

  const [isPrintOpen, setIsPrintOpen] = useState(false);

  const [queryDates, setQueryDates] = useState<{ startDate: Date; endDate: Date }>({
    startDate: moment(siteTime.today()).add(0, 'day').startOf('day').toDate(),
    endDate: siteTime.today().toLastNearestMinutes(5),
  });
  const [linkedTemperature, setLinkedTemperature] = useState<LinkedInstanceChartDataDto>();
  const [selectedEventLog, setSelectedEventLog] = useState<EventLog>();
  const [zoomDomain, setZoomDomain] = useState<{ x: [number, number] } | null>(null);
  const [isZoomMode, setIsZoomMode] = useState(false);
  // pendingZoomDomain mirrors the brush selection while the user is dragging.
  // Nothing zooms until they hit Apply — that's when we copy this into zoomDomain.
  const [pendingZoomDomain, setPendingZoomDomain] = useState<{ x: [number, number] } | null>(null);
  // Incremented every time the user opens zoom mode. Passed down to the chart
  // as a key on the brush container so Victory creates a fresh instance with
  // no carried-over selection.
  const [zoomSessionId, setZoomSessionId] = useState(0);

  const isWeb = Platform.OS === 'web';
  const isLiveRange = !moment(queryDates.endDate).isBefore(siteTime.today(), 'day');

  const { xlsxExport } = useExportTo();

  const queryParams = useMemo(() => {
    return { ...queryDates, siteId: siteId, equipmentId: equipmentFocus.EquipmentId };
  }, [queryDates, siteId, equipmentFocus.EquipmentId]);

  const queryChartData = useGetChartData(queryParams);
  const queryStoreEvents = useGetStoreEvents(queryParams);
  const queryEventCounts = useGetEventCounts(queryParams);

  const thresholds = equipmentFocus.TemperatureMetrics?.Temperature?.Thresholds;
  const sensorName = equipmentFocus.TemperatureMetrics?.Temperature?.Description;

  const mapSensorTypeById = useMemo(() => {
    const map: SensorTypeMap = {};
    const temp = equipmentFocus.TemperatureMetrics;
    const door = equipmentFocus.DoorMetrics;
    if (temp?.Temperature?.InstanceId) map[temp.Temperature.InstanceId] = 'temperature';
    if (temp?.RealTimeStatus?.InstanceId) map[temp.RealTimeStatus.InstanceId] = 'temperature';
    if (temp?.BatteryVoltage?.InstanceId) map[temp.BatteryVoltage.InstanceId] = 'temperature';
    if (door?.Door?.InstanceId) map[door.Door.InstanceId] = 'door';
    if (door?.RealTimeStatus?.InstanceId) map[door.RealTimeStatus.InstanceId] = 'door';
    if (door?.BatteryVoltage?.InstanceId) map[door.BatteryVoltage.InstanceId] = 'door';
    return map;
  }, [equipmentFocus.TemperatureMetrics, equipmentFocus.DoorMetrics]);

  const hasChartData = !isEmpty(linkedTemperature?.Temperature?.Data);

  // When a zoom is active, every export / print artifact is anchored to
  // the zoomed window rather than the originally fetched range.
  const effectiveStart = zoomDomain ? new Date(zoomDomain.x[0]) : queryDates.startDate;
  const effectiveEnd = zoomDomain ? new Date(zoomDomain.x[1]) : queryDates.endDate;
  const effectiveChartData = zoomDomain
    ? clampChartDataByRange(queryChartData.data, zoomDomain.x[0], zoomDomain.x[1], thresholds)
    : queryChartData.data;
  const effectiveStoreEventsData = zoomDomain
    ? clampStoreEventsByRange(queryStoreEvents.data, zoomDomain.x[0], zoomDomain.x[1])
    : queryStoreEvents.data;
  const effectiveLinkedTemperature = zoomDomain
    ? find<LinkedInstanceChartDataDto>(effectiveChartData, _ => !!_.Temperature)
    : linkedTemperature;

  const handleChangeRange = (next: { startDate: Date; endDate: Date }) => {
    setQueryDates(next);
    // Picking a new range invalidates the current zoom — reset.
    setZoomDomain(null);
    setIsZoomMode(false);
    setPendingZoomDomain(null);
  };

  const handleEnterZoomMode = () => {
    setIsZoomMode(true);
    setPendingZoomDomain(null);
    setZoomSessionId(prev => prev + 1);
  };

  const handleCancelZoom = () => {
    setIsZoomMode(false);
    setPendingZoomDomain(null);
    setZoomSessionId(prev => prev + 1);
  };

  const handleApplyZoom = () => {
    if (!pendingZoomDomain) return;
    setZoomDomain(pendingZoomDomain);
    setIsZoomMode(false);
    setPendingZoomDomain(null);
  };

  const handleResetZoom = () => {
    setZoomDomain(null);
    setIsZoomMode(false);
    setPendingZoomDomain(null);
  };

  const handleExportExcel = () => {
    if (!effectiveLinkedTemperature) return;
    const rawRows = buildEquipmentExportRows(effectiveChartData);
    if (rawRows.length === 0) return;

    const displayName = et(sensorName) ?? sensorName ?? 'equipment';
    const fileName = buildExportFileName(siteInfo?.name, displayName);
    const dateRangeLabel = `${moment(effectiveStart).format('MMM D, YYYY h:mm A')} — ${moment(effectiveEnd).format('MMM D, YYYY h:mm A')}`;
    const titleLine = `${displayName} — ${t('store_equipment:equipment_temperature_log')}`;

    const eqId = equipmentFocus.EquipmentId;
    const tempDesc = sensorName?.toLowerCase();
    const sensorRows = (hardwareSensors ?? []).filter((s: any) => s?.EquipmentId === eqId);
    const tempSensor = sensorRows.find((s: any) => s?.Label?.toLowerCase() === tempDesc);
    const doorSensor = sensorRows.find((s: any) => s !== tempSensor);
    const infoRows = buildEquipmentInfoRows({
      customerName: siteInfo?.customerName,
      storeName: siteInfo?.name,
      address: siteInfo?.address,
      equipmentName: displayName,
      temperatureSensor: tempSensor?.AdditionalProperties ? { ...tempSensor.AdditionalProperties, Serial: tempSensor.Serial } : undefined,
      doorSensor: doorSensor?.AdditionalProperties ? { ...doorSensor.AdditionalProperties, Serial: doorSensor.Serial } : undefined,
      dateRangeLabel,
      generatedBy: generatedBy,
      generatedAt: moment().format('MMM D, YYYY h:mm A'),
      stats: {
        ...effectiveLinkedTemperature?.Temperature?.Statistics,
        UpTimePct: effectiveLinkedTemperature?.RealTimeStatus?.Statistics?.UpTimePct,
      },
    });

    const eventLogRows = buildEventLogRows(effectiveStoreEventsData);

    const hasDoor = rawRows.some(r => r['Door (1=Closed, 2=Open)'] !== '');
    const readingsRows = rawRows.map(row => {
      const base: Record<string, number | string> = {
        Timestamp: row.Timestamp,
        'Temperature (°F)': row['Temp (°F)'],
        Signal: row['Signal (0=Offline, 1=Online)'] === '' ? '' : row['Signal (0=Offline, 1=Online)'] === 1 ? 'Online' : 'Offline',
      };
      if (hasDoor) {
        base.Door = row['Door (1=Closed, 2=Open)'] === '' ? '' : row['Door (1=Closed, 2=Open)'] === 1 ? 'Closed' : 'Open';
      }
      return base;
    });

    xlsxExport(fileName, [
      {
        sheet: 'Equipment Information',
        title: titleLine,
        jsonData: infoRows,
      },
      {
        sheet: 'Event Log',
        title: titleLine,
        jsonData:
          eventLogRows.length > 0
            ? eventLogRows
            : [
                {
                  'Event Type': '—',
                  'Violation Trigger': '—',
                  'Event Severity': '—',
                  'Start Time': '—',
                  'End Time': '—',
                  Duration: '—',
                },
              ],
      },
      {
        sheet: 'Temperature Readings',
        title: titleLine,
        jsonData: readingsRows,
      },
    ]);
  };

  useEffect(() => {
    const linkedTemperatureDto = find<LinkedInstanceChartDataDto>(queryChartData.data, _ => !!_.Temperature);
    setLinkedTemperature(linkedTemperatureDto);
  }, [queryChartData.data]);

  return (
    <VStack flex={1} ref={panelRef as any}>
      {/* zIndex bumps the chart panel above the Event Logs sibling below so
          the date/time popover can render on top of EventLogs. */}
      <VStack pb={2} space={2} zIndex={20}>
        {/* Title row */}
        <Box
          flexDirection="row"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          px={3}
          py={2}
          borderBottomWidth={1}
          zIndex={20}
          style={{ zIndex: 20, rowGap: 8 } as any}
        >
          <HStack space={2} alignItems="center">
            {/* List collapse toggle */}
            {onToggleList && (
              <IconButton
                variant="unstyled"
                rounded="md"
                p={0}
                boxSize={7}
                shadow={0}
                borderWidth={1}
                borderColor="gray.400"
                bg="white"
                _pressed={{ borderColor: 'gray.400', bg: 'gray.50' }}
                icon={
                  listCollapsed ? <ChevronRightDoubleIcon size={3} color="gray.700" /> : <ChevronLeftDoubleIcon size={3} color="gray.700" />
                }
                onPress={onToggleList}
                accessibilityLabel={listCollapsed ? 'Expand sensor list' : 'Collapse sensor list'}
              />
            )}

            <Box>
              <Text size={{ base: '2xl', xl: '4xl' }} color="black" fontWeight={600} lineHeight="xs">
                {et(sensorName)}
              </Text>
            </Box>

            {/* Thresholds */}
            <HStack space={1} alignItems="center">
              {/* Lower Red */}
              <HStack
                rounded="full"
                borderWidth={1}
                borderColor="gray.100"
                alignItems="center"
                p={1}
                pr={{ base: 1, xl: 2 }}
                space={{ base: 1, xl: 2 }}
              >
                <Box rounded="full" alignItems="center" justifyContent="center" p={0.5} bgColor="error.600">
                  <AlertOctagonIcon color="white" size={{ base: 2, xl: 4 }} />
                </Box>
                <Text size={{ base: 'sm', xl: 'md' }} color="gray.700" fontWeight={600} lineHeight="xs">
                  {`<${tempValue(thresholds?.LowerRed)}`}
                </Text>
              </HStack>

              {/* Lower and Upper Green */}
              <HStack rounded="full" borderWidth={1} borderColor="gray.100" alignItems="center" p={1} space={{ base: 1, xl: 2 }}>
                <Box rounded="full" bgColor="blueLight.500" alignItems="center" p={0.5}>
                  <OutlineBadgeCheckIcon color="white" size={{ base: 2, xl: 4 }} />
                </Box>
                <Text size={{ base: 'sm', xl: 'md' }} color="gray.700" fontWeight={600} lineHeight="xs">
                  {`${(thresholds?.LowerGreen ?? 0).toFixed(1)} - ${tempValue(thresholds?.UpperGreen)}`}
                </Text>
              </HStack>

              {/* Upper Red */}
              <HStack rounded="full" borderWidth={1} borderColor="gray.100" alignItems="center" p={1} space={{ base: 1, xl: 2 }}>
                <Box rounded="full" bgColor="error.600" alignItems="center" p={0.5}>
                  <AlertOctagonIcon color="white" size={{ base: 2, xl: 4 }} />
                </Box>
                <Text size={{ base: 'sm', xl: 'md' }} color="gray.700" fontWeight={600} lineHeight="xs">
                  {`>${tempValue(thresholds?.UpperRed)}`}
                </Text>
              </HStack>
            </HStack>
          </HStack>

          {/* Right side of the title row swaps between three states:
              - Normal: date/time picker + quick-pick pills
              - Zoomed: chip showing active zoom window with × to exit
              - Zoom MODE: "selecting zoom…" indicator while dragging */}
          <HStack space={2} alignItems="center">
            {zoomDomain && !isZoomMode ? (
              <HStack bg="blue.50" borderWidth={1} borderColor="blue.200" borderRadius="lg" px={3} py={2} space={2} alignItems="center">
                <ZoomInIcon size={5} color="blue.800" />
                <Text size="md" color="blue.700" fontWeight={600} lineHeight="sm">
                  {moment(zoomDomain.x[0]).format('MMM D, h:mm A')}
                </Text>
                <Text size="md" color="blue.500" fontWeight={500}>
                  →
                </Text>
                <Text size="md" color="blue.700" fontWeight={600} lineHeight="sm">
                  {moment(zoomDomain.x[1]).format('MMM D, h:mm A')}
                </Text>
                <IconButton
                  variant="ghost"
                  borderRadius="md"
                  p={0}
                  ml={1}
                  _hover={{ bg: 'blue.100' }}
                  accessibilityLabel={t('store_equipment:reset_zoom')}
                  onPress={handleResetZoom}
                  icon={<CloseIcon size={4} color="blue.800" />}
                />
              </HStack>
            ) : isZoomMode ? (
              <HStack bg="blue.50" borderWidth={1} borderColor="blue.200" borderRadius="lg" px={3} py={2} space={2} alignItems="center">
                <ZoomInIcon size={5} color="blue.800" />
                <Text size="md" color="blue.700" fontWeight={500} lineHeight="sm">
                  {t('store_equipment:selecting_zoom')}
                </Text>
              </HStack>
            ) : (
              <DateTimeRangeBar
                startDate={queryDates.startDate}
                endDate={queryDates.endDate}
                today={siteTime.today()}
                onChange={handleChangeRange}
                panelRef={panelRef}
              />
            )}
          </HStack>
        </Box>

        {/* Chart */}
        <VStack space={1} px={3} mb={{ xl: 1 }}>
          <HStack space={2} justifyContent="space-between" alignItems="center">
            {/* Chart Stats UI — during zoom, pulls from effectiveLinkedTemperature
                (clipped to the zoom window) so it matches what the export carries. */}
            <ChartStatistics
              statistics={effectiveLinkedTemperature?.Temperature?.Statistics}
              realTimeStatistics={effectiveLinkedTemperature?.RealTimeStatus?.Statistics}
            />

            {/* Chart toolbar — Zoom + Refresh + Print + Excel icon buttons */}
            <HStack space={2} alignItems="center" justifyContent="center">
              {!zoomDomain && !isZoomMode && (
                <IconButton
                  variant="outline"
                  borderColor="gray.300"
                  borderWidth={1}
                  borderRadius="lg"
                  width={10}
                  height={10}
                  _hover={{ bg: 'gray.100' }}
                  _focus={{ bg: 'transparent', borderColor: 'gray.300' }}
                  _focusVisible={{ bg: 'gray.100', borderColor: 'gray.300' }}
                  _pressed={{ bg: 'gray.200', borderColor: 'gray.300' }}
                  _disabled={{ opacity: 0.4 }}
                  isDisabled={!hasChartData}
                  accessibilityLabel={t('store_equipment:zoom')}
                  onPress={handleEnterZoomMode}
                  icon={<ZoomInIcon size={5} color="gray.700" />}
                />
              )}
              {isWeb && (
                <>
                  <IconButton
                    variant="outline"
                    borderColor="gray.300"
                    borderWidth={1}
                    borderRadius="lg"
                    width={10}
                    height={10}
                    _hover={{ bg: 'gray.100' }}
                    _focus={{ bg: 'transparent', borderColor: 'gray.300' }}
                    _focusVisible={{ bg: 'gray.100', borderColor: 'gray.300' }}
                    _pressed={{ bg: 'gray.200', borderColor: 'gray.300' }}
                    _disabled={{ opacity: 0.4 }}
                    isDisabled={!isLiveRange || queryChartData.isFetching || queryStoreEvents.isFetching}
                    accessibilityLabel={t('store_equipment:refresh')}
                    onPress={() => {
                      queryChartData.refetch();
                      queryStoreEvents.refetch();
                      queryEventCounts.refetch();
                    }}
                    icon={
                      queryChartData.isFetching || queryStoreEvents.isFetching ? (
                        <Spinner size="sm" color="gray.500" />
                      ) : (
                        <RefreshIcon size={5} color="gray.700" />
                      )
                    }
                  />

                  <IconButton
                    variant="outline"
                    borderColor="gray.300"
                    borderWidth={1}
                    borderRadius="lg"
                    width={10}
                    height={10}
                    _hover={{ bg: 'gray.100' }}
                    _focus={{ bg: 'transparent', borderColor: 'gray.300' }}
                    _focusVisible={{ bg: 'gray.100', borderColor: 'gray.300' }}
                    _pressed={{ bg: 'gray.200', borderColor: 'gray.300' }}
                    _disabled={{ opacity: 0.4 }}
                    isDisabled={!hasChartData}
                    accessibilityLabel={t('store_equipment:print_report')}
                    onPress={() => setIsPrintOpen(true)}
                    icon={<PrinterIcon size={5} color="gray.700" />}
                  />

                  <IconButton
                    variant="outline"
                    borderColor="gray.300"
                    borderWidth={1}
                    borderRadius="lg"
                    width={10}
                    height={10}
                    _hover={{ bg: 'gray.100' }}
                    _focus={{ bg: 'transparent', borderColor: 'gray.300' }}
                    _focusVisible={{ bg: 'gray.100', borderColor: 'gray.300' }}
                    _pressed={{ bg: 'gray.200', borderColor: 'gray.300' }}
                    _disabled={{ opacity: 0.4 }}
                    isDisabled={!hasChartData}
                    accessibilityLabel={t('store_equipment:export_to_excel')}
                    onPress={handleExportExcel}
                    icon={<MsExcelIcon width={20} height={20} />}
                  />
                </>
              )}
            </HStack>
          </HStack>

          {/* Zoom mode bar — hint + selected range readout + Cancel/Apply pair */}
          {isZoomMode && (
            <HStack
              bg="blue.50"
              borderWidth={1}
              borderColor="blue.200"
              px={2}
              py={2}
              borderRadius="md"
              alignItems="center"
              justifyContent="space-between"
              space={3}
            >
              {pendingZoomDomain ? (
                <HStack flex={1} alignItems="center" space={2}>
                  <Text size="md" color="blue.700" fontWeight={600}>
                    {moment(pendingZoomDomain.x[0]).format('MMM D, h:mm A')}
                  </Text>
                  <Text size="md" color="blue.500" fontWeight={500}>
                    →
                  </Text>
                  <Text size="md" color="blue.700" fontWeight={600}>
                    {moment(pendingZoomDomain.x[1]).format('MMM D, h:mm A')}
                  </Text>
                </HStack>
              ) : (
                <Text size="md" color="blue.700" fontWeight={500} flex={1}>
                  {t('store_equipment:zoom_hint')}
                </Text>
              )}
              <HStack space={2} alignItems="center">
                <Button size="sm" variant="unstyled" borderColor="gray.300" bg="white" shadow={1} onPress={handleCancelZoom}>
                  <Text size="md" fontWeight={600} color="gray.900">
                    {t('app:cancel')}
                  </Text>
                </Button>
                <Button size="sm" shadow={0} isDisabled={!pendingZoomDomain} onPress={handleApplyZoom}>
                  <Text size="md" fontWeight={600} color="black">
                    {t('app:apply')}
                  </Text>
                </Button>
              </HStack>
            </HStack>
          )}

          {/* Line Chart */}
          <ResourceLoader
            minHeight={{ base: 300, xl: 365 }}
            isLoading={queryChartData.isIdle || queryChartData.isLoading}
            isEmpty={(queryChartData.isSuccess && isEmpty(linkedTemperature?.Temperature?.Data)) || queryChartData.isError}
            emptyMessage={
              <Text size="lg" fontWeight={600} color="gray.500" textAlign="center">
                {t('store_equipment:no_data_found')}
              </Text>
            }
          >
            <Box minHeight={{ base: 280, xl: 365 }} justifyContent="center" alignItems="center" position="relative">
              <TemperatureChart
                _container={{ maxHeight: { lg: 245, xl: undefined } }}
                startDate={queryDates.startDate}
                endDate={queryDates.endDate}
                linkedInstances={queryChartData.data}
                thresholds={thresholds}
                eventLog={selectedEventLog}
                zoomDomain={zoomDomain}
                isZoomMode={isZoomMode}
                zoomSessionId={zoomSessionId}
                pendingZoomDomain={pendingZoomDomain}
                onBrushDomainChange={domain => {
                  if (!domain) {
                    setPendingZoomDomain(null);
                    return;
                  }
                  setPendingZoomDomain({
                    x: [snapToFiveMinutes(domain.x[0]), snapToFiveMinutes(domain.x[1])],
                  });
                }}
              />

              {/* Floating Reset zoom overlay */}
              {zoomDomain && !isZoomMode && (
                <Box position="absolute" top={2} right={3} zIndex={10}>
                  <Button
                    size="sm"
                    variant="unstyled"
                    borderColor="blue.200"
                    bg="blue.50"
                    shadow={2}
                    _hover={{ bg: 'blue.100' }}
                    onPress={handleResetZoom}
                  >
                    <HStack space={1.5} alignItems="center">
                      <ZoomOutIcon size={4} color="blue.800" />
                      <Text size="sm" fontWeight={600} color="blue.700">
                        {t('store_equipment:reset_zoom')}
                      </Text>
                    </HStack>
                  </Button>
                </Box>
              )}
            </Box>
          </ResourceLoader>
        </VStack>
      </VStack>

      <Divider mt={2} bg="gray.200" />

      {/* Event Logs */}
      <Box flex={1} px={3}>
        <EventLogs
          sensorTypeMap={mapSensorTypeById}
          queryStoreEvents={queryStoreEvents}
          queryEventCounts={queryEventCounts}
          onSelectEvent={setSelectedEventLog}
          zoomDomain={zoomDomain}
        />
      </Box>

      {isWeb && (
        <Modal isOpen={isPrintOpen} size="xl" hideClose noPadding onClose={() => setIsPrintOpen(false)} _content={{ overflow: 'hidden' }}>
          <ReportPrintView
            equipmentFocus={equipmentFocus}
            chartData={effectiveChartData}
            hardwareSensors={hardwareSensors}
            startDate={effectiveStart}
            endDate={effectiveEnd}
            siteInfo={siteInfo}
            storeEvents={effectiveStoreEventsData}
            generatedBy={generatedBy}
            onClose={() => setIsPrintOpen(false)}
          />
        </Modal>
      )}
    </VStack>
  );
};
